/**
 * The day: types, the API calls, and a small store that keeps the screen
 * responsive while writes are in flight.
 *
 * Live cell editing means the clerk's typing must never wait for the network.
 * So: apply the change locally at once, debounce ~800ms, send what changed with
 * the `rowVersion` we were looking at, and take the server's figures as final
 * when they come back — it is the only thing that does the arithmetic.
 *
 * A 409 means somebody else wrote first (the owner approving mid-shift is the
 * normal case, not an edge one). We reload the day rather than clobber it.
 */

import { ApiError, request, uploadBlob, uuidv7 } from './api';
import { isNetworkError } from './connectivity';
import { dayGet, daySet, dayLatest, kvGet } from './store';
import type { Op } from './offline';
import { applyLocally, synthesiseDay, toWire } from './offline';
import { enqueue, queuedFor } from './sync';
import { getSession } from './session';
import { payWeekEndingOf } from './calendar';
import { formatRs } from './money';

export type Attendance = 'worked' | 'absent' | null;
export type PayBasis = 'day' | 'trip';
export type SectionName = 'labour' | 'diesel' | 'misc';
export type SectionStatus = 'open' | 'submitted' | 'approved';

export interface AdvanceRow {
  id: string;
  amountRs: number;
  note: string | null;
  payWeekEnding: string;
  signed: boolean;
  signedAt: string | null;
  /** Who handed the cash over — printed on the receipt. */
  paidByName: string;
  rowVersion: number;
  /** Written with no signal; the Worker has not seen it yet. */
  queued?: boolean;
}

export interface RoleOption {
  id: string;
  name: string;
  baseRs: number;
  addonRs: number;
}

export interface EntryRow {
  workerId: string;
  workerName: string;
  sortOrder: number;
  attendance: Attendance;
  jobRoleId: string | null;
  roleName: string | null;
  baseRs: number;
  addonRs: number;
  dailyRateRs: number;
  payBasis: PayBasis;
  hoursTenths: number;
  otTenths: number;
  allowanceRs: number;
  sundayAllowanceRs: number;
  allowanceReason: string | null;
  salaryRs: number;
  otRs: number;
  earnedRs: number;
  baseEarnedRs: number;
  isEpfMember: boolean;
  /** Starts as his usual code; the office changes it when his day was for something else. */
  costCodeId: string | null;
  /** His own code as Settings has it now — "usually CC-03" when today's differs. */
  usualCostCodeId?: string | null;
  status: string;
  finalised: boolean;
  rejected: boolean;
  rejectNote: string | null;
  rowVersion: number;
  /** Every role he holds, to pick from. `roleName` is what he did today. */
  roles: RoleOption[];
  /** The same, joined for the receipt: "Excavator operator / Tipper driver". */
  rolesLabel: string;
  /** What he has drawn across the whole pay week, including today. */
  advancesThisWeekRs: number;
  advances: AdvanceRow[];
  /** Edited with no signal: the figures are the last the Worker computed. */
  queued?: boolean;
}

export interface SectionRow {
  section: SectionName;
  status: SectionStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  /** Who sent it and who approved it — stamped on the day report. Absent offline. */
  submittedBy?: string | null;
  submittedByName?: string | null;
  approvedBy?: string | null;
  approvedByName?: string | null;
  unsignedOverrideAt: string | null;
  rowVersion: number;
  queued?: boolean;
}

export interface FuelRow {
  plant_id: string;
  litres_x100: number;
  rate_rs: number;
  value_rs: number;
  /** Starts as the plant's usual code. Absent on a row cached before cost codes. */
  cost_code_id?: string | null;
  row_version: number;
  queued?: boolean;
  /** From the Worker only; a row applied offline has none. */
  plant_code?: string;
  plant_name?: string;
  plant_active?: number;
}

export interface MiscRow {
  id: string;
  description: string;
  amount_rs: number;
  category_id: string;
  category_name: string;
  requires_plant: number;
  plant_id: string | null;
  cost_code_id?: string | null;
  receipt_key: string | null;
  row_version: number;
  queued?: boolean;
  /** From the Worker only; names a plant even after it was removed. */
  plant_code?: string;
  plant_name?: string;
  plant_active?: number;
}

export interface DayTotals {
  labourRs: number;
  dieselRs: number;
  litresX100: number;
  miscRs: number;
  advancesRs: number;
  costRs: number;
  cashOutRs: number;
  workersOnSite: number;
  workersAbsent: number;
}

export interface DayView {
  day: {
    workDate: string;
    isSunday: boolean;
    dieselRateRs: number;
    hasData: boolean;
    closedAt: string | null;
    /** Who approved and closed the day — "Approved by (owner)" on the report. */
    closedBy?: string | null;
    closedByName?: string | null;
    opened: boolean;
    rowVersion: number;
  };
  sections: SectionRow[];
  entries: EntryRow[];
  fuel: FuelRow[];
  misc: MiscRow[];
  totals: DayTotals;
  blockingDay: string | null;
  /** How many writes for this day are waiting for a signal. */
  queued?: number;
  /** Built on the tablet from the last day it saw, never loaded from the Worker. */
  synthesised?: boolean;
}

/** The fields a cell edit can carry. */
export interface EntryPatch {
  attendance?: Attendance;
  jobRoleId?: string;
  payBasis?: PayBasis;
  hoursTenths?: number;
  otTenths?: number;
  allowanceRs?: number;
  sundayAllowanceRs?: number;
  allowanceReason?: string | null;
  costCodeId?: string | null;
}

/**
 * Sends an op, or queues it.
 *
 * Live, this is the request the op maps to, nothing more. With no signal the op
 * goes into the outbox with the id it will be replayed under, the cached day is
 * patched so the clerk sees what she typed, and `fallback` shapes the answer the
 * caller was expecting — from the patched day, so it agrees with the screen.
 * Nothing here decides money: a queued row keeps its last figures until the
 * Worker has seen it (CLAUDE.md rule 12).
 */
async function send<T>(
  op: Op,
  label: string,
  fallback: (view: DayView) => T,
  blob?: Blob,
  contentType?: string,
): Promise<T> {
  const wire = toWire(op);
  try {
    if (blob) return await uploadBlob<T>(wire.path, blob, contentType ?? 'application/octet-stream');
    return await request<T>(wire.path, { method: wire.method, body: wire.body });
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    const at = new Date().toISOString();
    await enqueue(op, label, blob, contentType);
    const cached = (await dayGet<DayView>(op.date)) ?? (await synthesised(op.date));
    if (!cached) throw new Error('This tablet has no copy of that day to write into.');
    const next = applyLocally(cached, op, at);
    await daySet(op.date, next);
    return fallback(next);
  }
}

/** A day never seen while there was signal, from the last one that was. */
async function synthesised(date: string): Promise<DayView | null> {
  const latest = await dayLatest<DayView>();
  return latest ? synthesiseDay(date, latest.view) : null;
}

const entryIn = (view: DayView, workerId: string): EntryRow => {
  const row = view.entries.find((e) => e.workerId === workerId);
  if (!row) throw new Error('That worker is not on this day.');
  return row;
};

const nameOf = (view: DayView, workerId: string): string =>
  view.entries.find((e) => e.workerId === workerId)?.workerName ?? 'Worker';

interface CachedReference {
  miscCategories: { id: string; name: string; requires_plant: number }[];
}

export const dayApi = {
  /**
   * The day as the Worker has it, with anything still queued for it laid on
   * top; or, with no signal, the tablet's copy. A day the tablet has never
   * seen is built from the last one it saw — the roster does not change
   * between one morning and the next often enough to wait for a signal.
   */
  get: async (date: string): Promise<DayView> => {
    let view: DayView;
    try {
      view = await request<DayView>(`/days/${date}`);
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      const cached = (await dayGet<DayView>(date)) ?? (await synthesised(date));
      if (!cached) throw new Error('No signal, and this tablet has no copy of that day yet.');
      return cached;
    }
    for (const queued of await queuedFor(date)) view = applyLocally(view, queued.op, queued.at);
    await daySet(date, view);
    return view;
  },

  patchEntry: (date: string, workerId: string, patch: EntryPatch & { rowVersion: number }) => {
    const { rowVersion, ...fields } = patch;
    return send<Partial<EntryRow> & { rowVersion: number }>(
      { kind: 'entry.patch', date, workerId, patch: fields, rowVersion },
      `${date} — timesheet edit`,
      (view) => {
        const row = entryIn(view, workerId);
        return { ...fields, rowVersion: row.rowVersion, queued: true };
      },
    );
  },

  finalise: (date: string, workerId: string) =>
    send<{ workerId: string; finalised: boolean; needsSignature: boolean }>(
      { kind: 'entry.finalise', date, workerId },
      `${date} — finalise`,
      (view) => ({
        workerId,
        finalised: true,
        needsSignature: entryIn(view, workerId).advances.some((a) => !a.signed),
      }),
    ),

  unfinalise: (date: string, workerId: string) =>
    send<{ workerId: string; finalised: boolean }>(
      { kind: 'entry.unfinalise', date, workerId },
      `${date} — undo finalise`,
      () => ({ workerId, finalised: false }),
    ),

  addAdvance: async (date: string, workerId: string, amountRs: number, note?: string) => {
    const advanceId = uuidv7();
    const cached = await dayGet<DayView>(date);
    const label = `${cached ? nameOf(cached, workerId) : 'Worker'} — advance ${formatRs(amountRs)}`;
    return send<AdvanceRow>(
      {
        kind: 'advance.add', date, workerId, advanceId, amountRs, note: note ?? null,
        payWeekEnding: payWeekEndingOf(date), paidByName: getSession()?.user.name ?? '',
      },
      label,
      (view) => entryIn(view, workerId).advances.find((a) => a.id === advanceId)!,
    );
  },

  voidAdvance: (date: string, id: string, reason?: string) =>
    send<void>(
      { kind: 'advance.void', date, advanceId: id, reason: reason ?? null },
      `${date} — cancel an advance`,
      () => undefined,
    ),

  /** The captured PNG, straight to R2 by way of the Worker. */
  saveSignature: (date: string, id: string, png: Blob) =>
    send<{ id: string; signed: boolean }>(
      { kind: 'signature.put', date, advanceId: id },
      `${date} — signature`,
      () => ({ id, signed: true }),
      png,
      'image/png',
    ),

  /** A path, not a URL: fetch it with `fetchImage` so it gets the base and the token. */
  signatureUrl: (id: string) => `/advances/${id}/signature`,

  setRate: (date: string, dieselRateRs: number) =>
    send<{ dieselRateRs: number }>(
      { kind: 'rate.set', date, dieselRateRs },
      `${date} — diesel rate ${formatRs(dieselRateRs)}`,
      () => ({ dieselRateRs }),
    ),

  setFuel: (date: string, plantId: string, litresX100: number) =>
    send<{ plantId: string; litresX100: number; rateRs: number; valueRs: number }>(
      { kind: 'fuel.set', date, plantId, litresX100 },
      `${date} — fuel issued`,
      (view) => {
        const row = view.fuel.find((f) => f.plant_id === plantId);
        return { plantId, litresX100, rateRs: row?.rate_rs ?? view.day.dieselRateRs, valueRs: row?.value_rs ?? 0 };
      },
    ),

  /** The day's cost code for a plant's fuel, apart from the litres. */
  setFuelCode: (date: string, plantId: string, costCodeId: string | null) =>
    send<{ plantId: string; costCodeId: string | null }>(
      { kind: 'fuel.code', date, plantId, costCodeId },
      `${date} — cost code for fuel`,
      () => ({ plantId, costCodeId }),
    ),

  plantAccount: (plantId: string, on: string) =>
    request<{
      plantId: string;
      last7: { litresX100: number; dieselRs: number; miscRs: number; totalRs: number };
      last30: { litresX100: number; dieselRs: number; miscRs: number; totalRs: number };
      days: { work_date: string; litres_x100: number; diesel_rs: number; misc_rs: number; total_rs: number }[];
      miscItems: { work_date: string; description: string; amount_rs: number }[];
    }>(`/plants/${plantId}/account?on=${on}`),

  addMisc: async (
    date: string,
    body: {
      description: string; amountRs: number; categoryId: string; plantId: string | null;
      costCodeId: string | null;
    },
  ) => {
    const miscId = uuidv7();
    // The category's name and plant flag are snapshotted onto the row by the
    // Worker; offline they come from the cached reference list so the row
    // reads the same before and after sync.
    const reference = await kvGet<CachedReference>('reference');
    const category = reference?.miscCategories.find((c) => c.id === body.categoryId);
    return send<{ id: string; rowVersion: number }>(
      {
        kind: 'misc.add', date, miscId, description: body.description, amountRs: body.amountRs,
        categoryId: body.categoryId, categoryName: category?.name ?? '', requiresPlant: category?.requires_plant ?? 0,
        plantId: body.plantId, costCodeId: body.costCodeId,
      },
      `${date} — ${body.description} ${formatRs(body.amountRs)}`,
      () => ({ id: miscId, rowVersion: 1 }),
    );
  },

  updateMisc: async (
    date: string,
    id: string,
    body: {
      description?: string; amountRs?: number; categoryId?: string; plantId?: string | null;
      costCodeId?: string | null; rowVersion: number;
    },
  ) => {
    const { rowVersion, ...patch } = body;
    const reference = await kvGet<CachedReference>('reference');
    const category = patch.categoryId
      ? reference?.miscCategories.find((c) => c.id === patch.categoryId)
      : undefined;
    return send<{ id: string; rowVersion: number }>(
      {
        kind: 'misc.update', date, miscId: id, rowVersion,
        patch: {
          ...patch,
          ...(category ? { categoryName: category.name, requiresPlant: category.requires_plant } : {}),
        },
      },
      `${date} — expense edit`,
      (view) => ({ id, rowVersion: view.misc.find((m) => m.id === id)?.row_version ?? rowVersion + 1 }),
    );
  },

  deleteMisc: (date: string, id: string) =>
    send<void>({ kind: 'misc.delete', date, miscId: id }, `${date} — remove an expense`, () => undefined),

  /** The compressed JPEG, straight to R2 by way of the Worker. */
  saveReceipt: (date: string, id: string, jpeg: Blob) =>
    send<{ id: string; receipt: boolean }>(
      { kind: 'receipt.put', date, miscId: id },
      `${date} — receipt photo`,
      () => ({ id, receipt: true }),
      jpeg,
      'image/jpeg',
    ),

  receiptUrl: (id: string) => `/misc/${id}/receipt`,

  submit: (date: string, section: SectionName, overrideUnsigned = false) =>
    send<{ section: SectionName; status: SectionStatus; submittedAt: string }>(
      { kind: 'section.submit', date, section, overrideUnsigned },
      `${date} — send ${section} to the owner`,
      (view) => ({
        section,
        status: 'submitted',
        submittedAt: view.sections.find((s) => s.section === section)?.submittedAt ?? '',
      }),
    ),

  pullBack: (date: string, section: SectionName) =>
    send<{ section: SectionName; status: SectionStatus }>(
      { kind: 'section.pullback', date, section },
      `${date} — pull ${section} back`,
      () => ({ section, status: 'open' }),
    ),
};

// ---------------------------------------------------------------- helpers

export function section(view: DayView, name: SectionName): SectionRow {
  return (
    view.sections.find((row) => row.section === name) ?? {
      section: name, status: 'open', submittedAt: null, approvedAt: null,
      unsignedOverrideAt: null, rowVersion: 1,
    }
  );
}

/** Can the clerk still type into this section? */
export function sectionEditable(view: DayView, name: SectionName, isOwner: boolean): boolean {
  if (view.day.closedAt) return false;
  const row = section(view, name);
  if (row.status === 'approved') return false;
  if (row.status === 'submitted' && !isOwner) return false;
  return true;
}

/** The prototype's `counted()`, corrected: an absent man earns nothing. */
export function counted(entry: EntryRow): boolean {
  return entry.attendance === 'worked';
}

export function entered(entry: EntryRow): boolean {
  return entry.attendance !== null;
}

export function advanceTotal(entry: EntryRow): number {
  return entry.advances.reduce((sum, advance) => sum + advance.amountRs, 0);
}

export function allSigned(entry: EntryRow): boolean {
  return entry.advances.length > 0 && entry.advances.every((advance) => advance.signed);
}

/** Any allowance above zero needs a reason — either box. */
export function needsReason(entry: EntryRow): boolean {
  return (
    (entry.allowanceRs > 0 || entry.sundayAllowanceRs > 0) &&
    (entry.allowanceReason ?? '').trim().length === 0
  );
}

/**
 * The roles a row can be switched to. His active roles, plus the one the row
 * already names if it has since been taken off him — so an old day still shows
 * what it was paid under instead of a blank.
 */
export function roleOptions(entry: EntryRow): RoleOption[] {
  const held = entry.roles ?? [];
  if (entry.jobRoleId && !held.some((r) => r.id === entry.jobRoleId)) {
    return [
      ...held,
      { id: entry.jobRoleId, name: entry.roleName ?? 'Former role', baseRs: entry.baseRs, addonRs: entry.addonRs },
    ];
  }
  return held;
}
